/**
 * Pure multi-town zone geometry — which service zone (if any) a point falls
 * in. Mirrors rideSecurity.js's server-side resolveZone exactly (same
 * "check the zone list in order, first match wins" rule) so the passenger
 * app's map/messaging never disagrees with what the server will actually
 * accept a booking in.
 *
 * Kept dependency-free (no Maps SDK, no APP singleton) so it's unit
 * testable — services/maps.js supplies the live zone list from APP.
 */
const toRad = (d) => (d * Math.PI) / 180;

/** Great-circle distance in metres. Duplicated from maps.js's distanceBetweenM on purpose — that file isn't import-safe from a plain Node test. */
const distanceM = (a, b) => {
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

/**
 * `zones` is the effective list — the default zone first, then any extra
 * towns — same shape as rideSecurity.js builds: {id, name, center:{lat,lng},
 * radiusKm, active}. Returns the first containing zone, or null.
 */
export const findZone = (zones, lat, lng) => {
  for (const zone of zones) {
    if (zone?.center && distanceM(zone.center, { lat, lng }) <= zone.radiusKm * 1000) return zone;
  }
  return null;
};

/**
 * May a GPS fix move the app to the town it falls in? Yes, unless the rider
 * picked a town in this session (the town chooser, or "Use my location") and
 * this fix is only a background one. A town remembered from an earlier launch
 * is not a pick in this session, so GPS corrects it: a Piler rider who opens
 * the app in Kalikiri lands on Kalikiri, not on "outside the service area".
 */
export const gpsMayChangeTown = ({ pickedThisSession, explicit }) => Boolean(explicit) || !pickedThisSession;

/**
 * Geocoder query with the town on screen appended, e.g. "bus stand" ->
 * "bus stand, Kalikiri, Andhra Pradesh". Left as typed when it already names
 * the town (any case); a missing town or state is simply left out.
 */
export const withTownContext = (query, city, state) => {
  const text = String(query || '').trim();
  const town = String(city || '').trim();
  if (!text || !town || text.toLowerCase().includes(town.toLowerCase())) return text;
  const region = String(state || '').trim();
  return region ? `${text}, ${town}, ${region}` : `${text}, ${town}`;
};
